import { logger } from "./logger";
import { getVoiceLang } from "./voicePref";
// Sound utilities using Web Speech API for text-to-speech
// VOICE: respects user choice (en-US default, or id-ID Indonesian voice reading English text)

// Cached voice picks for both languages
let _enVoice = null;
let _idVoice = null;

const pickVoice = (langCode) => {
  if (langCode === "id-ID" && _idVoice) return _idVoice;
  if (langCode === "en-US" && _enVoice) return _enVoice;
  try {
    const voices = window.speechSynthesis.getVoices();
    let v = voices.find(x => x.lang === langCode);
    if (!v) v = voices.find(x => x.lang && x.lang.startsWith(langCode.split("-")[0]));
    if (langCode === "id-ID") _idVoice = v;
    if (langCode === "en-US") _enVoice = v;
    return v;
  } catch (err) { logger.debug("pickVoice failed", err); }
  return null;
};

export const speakText = (text, _ignoredLang) => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const lang = getVoiceLang();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.85;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    const v = pickVoice(lang);
    if (v) utterance.voice = v;
    window.speechSynthesis.speak(utterance);
  }
};

export const announcePlayerTurn = (playerName) => {
  speakText(`${playerName}, your turn!`);
};

export const announceWinner = (winnerName) => {
  speakText(`Congratulations! ${winnerName} wins!`);
};

export const announceDraw = () => {
  speakText(`It's a draw! Great game!`);
};

// Preload voices (needed for some browsers)
export const initSpeech = () => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => {
      _enVoice = null;
      _idVoice = null;
      window.speechSynthesis.getVoices();
      pickVoice("en-US");
      pickVoice("id-ID");
    };
  }
};

// Thunder/Lightning sound effect using Audio Context
export const playThunderSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create noise for thunder
    const bufferSize = audioContext.sampleRate * 0.3; // 0.3 second
    const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Generate thunder-like noise with decay
    for (let i = 0; i < bufferSize; i++) {
      const decay = Math.exp(-i / (bufferSize * 0.15));
      data[i] = (Math.random() * 2 - 1) * decay;
    }
    
    // Create source and gain
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    
    // Low pass filter for deeper thunder sound
    const filter = audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 150;
    
    // Gain for volume control
    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0.4;
    
    // Connect nodes
    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    source.start();
    
    // Cleanup after sound finishes
    setTimeout(() => {
      audioContext.close();
    }, 500);
  } catch (err) { logger.debug("playWinSound failed", err); }
};

// Electric zap sound
export const playZapSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create oscillator for zap
    const oscillator = audioContext.createOscillator();
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.1);
    
    // Gain envelope
    const gainNode = audioContext.createGain();
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    
    // Distortion for electric effect
    const distortion = audioContext.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
      const x = (i / 128) - 1;
      curve[i] = Math.tanh(x * 3);
    }
    distortion.curve = curve;
    
    oscillator.connect(distortion);
    distortion.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.15);
    
    setTimeout(() => {
      audioContext.close();
    }, 300);
  } catch (err) { logger.debug("playZapSound failed", err); }
};
